import { Component, inject, computed, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { AuthService } from '../../../core/auth/auth.service';
import { BrandingService } from '../../services/branding/branding.service';
import { CartService } from '../../../core/services/cart.service';
import { UserService } from '../../../core/services/user.service';
import { ProductService, NavigationCategoryDto } from '../../../core/services/product.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { toSignal } from '@angular/core/rxjs-interop';


@Component({
  selector: 'app-top-nav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatBadgeModule,
  ],
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss'
})
export class TopNavComponent {
  private productService = inject(ProductService);
  private organizationService = inject(OrganizationService);
  private authService = inject(AuthService);
  private brandingService = inject(BrandingService);
  private cartService = inject(CartService);
  private userService = inject(UserService);
  private router = inject(Router);

  // Convert observable to signal for authentication state
  private auth0IsAuthenticated = toSignal(this.authService.isAuthenticated$, { initialValue: false });

  // Computed signals
  // Check both Auth0 authentication AND user data from UserService
  // This ensures anonymous users (who have user data but aren't Auth0-authenticated) are recognized
  isAuthenticated = computed(() => {
    const auth0Auth = this.auth0IsAuthenticated();
    const hasUserData = !!this.userService.getCurrentUser();
    return auth0Auth || hasUserData;
  });

  // Check if current user is anonymous
  // Relies on the UserService which fetches user data from the API
  isAnonymousUser = computed(() => {
    const userFromService = this.userService.getCurrentUser();

    // If user data is loaded, use the anonUser flag
    if (userFromService) {
      return userFromService.anonUser ?? false;
    }

    // While user data is loading, don't assume anonymous
    // This prevents showing "Anonymous User" briefly for non-anonymous users
    return false;
  });

  // Branding computed signals
  logoUrl = this.brandingService.logoUrl;
  appName = signal('SKUWorld');

  // Cart item count signal
  cartItemCount = computed(() => this.cartService.cartItemCount());

  // Dynamic Navigation categories
  navigationCategories = signal<NavigationCategoryDto[]>([]);

  constructor() {
    this.loadNavigation();
  }

  loadNavigation(): void {
    const distributorIdStr = this.organizationService.currentOrganizationId();
    if (!distributorIdStr) return;

    const distributorId = parseInt(distributorIdStr);
    this.productService.getNavigationCategories(distributorId).subscribe({
      next: (categories) => {
        this.navigationCategories.set(categories);
      },
      error: (err) => {
        console.error('Failed to load navigation in top-nav', err);
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }

  switchToMemberLogin(): void {
    // Clear anonymous login session
    localStorage.removeItem('anonymous_login');
    localStorage.removeItem('member_login');
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');

    // Clear Auth0 cache
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('@@auth0spajs@@')) {
        localStorage.removeItem(key);
      }
    });

    // Clear user service data
    this.userService.clear();

    // Redirect to login page with member mode enabled
    this.router.navigate(['/'], { queryParams: { mode: 'member' } });
  }

  /**
   * Handle mobile search input
   * Navigates to shop page with search query parameter
   */
  onMobileSearch(searchTerm: string): void {
    if (searchTerm.trim()) {
      this.router.navigate(['/shop'], {
        queryParams: { search: searchTerm.trim() }
      });
    }
  }
}
